import React, { useRef } from 'react';
import { observer } from 'mobx-react';

import { COLORS, ICONS } from "shared/constants";
import { useMedia, useOnClickOutside, useStore } from "shared/hooks";
import { OnChangeHandlerType } from "shared/types";

import { UiIcon } from '../UiIcon';
import { UiModal } from '../UiModal';

import './styles.scss';

type ValueType = null | string | number | undefined;
type MultipleValueType = Array<string | number>;
type DefaultItemType = {
    id: number | string | null,
    name: string
} | null;
type ItemType = {
    id: ValueType,
    name: string,
    nameFormatted?: string
};
type GroupType = {
    name: string,
    title: string | React.ReactNode,
    mode: 'single' | 'multiple',
    value: ValueType | MultipleValueType,
    defaultItem?: DefaultItemType,
    items?: ItemType[],
    onChange: OnChangeHandlerType<any>
};
type PropsType = {
    value?: ValueType,
    defaultItem?: DefaultItemType,
    label?: string | React.ReactNode,
    items?: ItemType[],
    groups?: GroupType[],
    onResetAll?: () => void,
    onChange?: (data: { value: ValueType, name: string }) => void
}

const defaultFilterItem = {
    id: null,
    name: 'Все'
};

const isEmptySingleValue = (value: GroupType['value']) => value === null || value === undefined || value === '';

const isDefaultSingleValue = (value: GroupType['value'], defaultItem: DefaultItemType) => {
    if (!defaultItem) {
        return isEmptySingleValue(value);
    }

    if (value === defaultItem.id) {
        return true;
    }

    return isEmptySingleValue(value) && defaultItem.id === null;
};

export const UiFilter = observer((
    {
        value = null,
        label,
        defaultItem = defaultFilterItem,
        items = [],
        groups = [],
        onResetAll,
        onChange
    }: PropsType
) => {
    const store = useStore(() => ({
        isOpened: false,
    }));
    const { isMobile, isMobileOrTablet } = useMedia();
    const ref = useRef(null);
    const isGrouped = groups.length > 0;
    const isGroupedMobile = isGrouped && isMobileOrTablet;

    useOnClickOutside(ref, () => {
        if (isGroupedMobile) {
            return;
        }

        store.set("isOpened", false);
    });

    const handleToggle = () => {
        if (isMobile && !isGrouped) {
            return;
        }

        store.set("isOpened", !store.isOpened);
    };

    const handleChange = (nextValue: ValueType) => {
        store.set("isOpened", false);
        onChange && onChange({
            value: nextValue,
            name: 'ui-filter'
        });
    };

    const handleGroupSingleChange = (group: GroupType, nextValue: ValueType) => {
        group.onChange({
            control: 'ui-filter',
            name: group.name,
            value: nextValue,
        });
    };

    const handleGroupMultipleChange = (group: GroupType, itemId: string | number) => {
        const groupValue = Array.isArray(group.value) ? group.value : [];
        const nextValue = groupValue.indexOf(itemId) > -1
            ? groupValue.filter(valueItem => valueItem !== itemId)
            : [...groupValue, itemId];

        group.onChange({
            control: 'ui-filter',
            name: group.name,
            value: nextValue,
        });
    };

    const handleClose = () => {
        store.set("isOpened", false);
    };

    const handleResetAll = () => {
        handleClose();
        onResetAll && onResetAll();
    };

    const isGroupedResetState = groups.every(group => {
        if (group.mode === 'multiple') {
            return Array.isArray(group.value) && group.value.length === 0;
        }

        return isDefaultSingleValue(group.value, group.defaultItem === undefined ? defaultItem : group.defaultItem);
    });

    const renderGroupedItems = (className = '') => (
            <div className={`ui-filter__items ui-filter__items--grouped ${className}`.trim()}>
            <div className="ui-filter-item ui-filter-item--title" onClick={handleResetAll}>
                <div className="ui-filter-item__name">Все</div>
                {isGroupedResetState && (
                    <div className="ui-filter-item__icon">
                        <UiIcon size={13} icon={ICONS.CHECK_CIRCLE}/>
                    </div>
                )}
            </div>
            {groups.map(group => {
                const groupDefaultItem = group.defaultItem === undefined ? defaultItem : group.defaultItem;
                const groupMultipleValue = Array.isArray(group.value) ? group.value : [];

                return (
                    <div className="ui-filter__group" key={group.name}>
                        <div className="ui-filter-item ui-filter-item--section">
                            <div className="ui-filter-item__name">{group.title}</div>
                        </div>
                        {group.mode === 'single' && groupDefaultItem && (
                            <div
                                className="ui-filter-item"
                                onClick={() => handleGroupSingleChange(group, groupDefaultItem.id)}
                            >
                                <div className="ui-filter-item__name">{groupDefaultItem.name}</div>
                                {isDefaultSingleValue(group.value, groupDefaultItem) && (
                                    <div className="ui-filter-item__icon">
                                        <UiIcon size={13} icon={ICONS.CHECK_CIRCLE}/>
                                    </div>
                                )}
                            </div>
                        )}
                        {group.items?.map(item => {
                            const isSelected = group.mode === 'multiple'
                                ? groupMultipleValue.indexOf(item.id as string | number) > -1
                                : item.id === group.value;

                            return (
                                <div
                                    key={`${group.name}-${item.id}`}
                                    className="ui-filter-item"
                                    onClick={() => {
                                        if (group.mode === 'multiple') {
                                            handleGroupMultipleChange(group, item.id as string | number);
                                            return;
                                        }

                                        handleGroupSingleChange(group, item.id);
                                    }}
                                >
                                    <div className="ui-filter-item__name">{item.nameFormatted ? item.nameFormatted : item.name}</div>
                                    {isSelected && (
                                        <div className="ui-filter-item__icon">
                                            <UiIcon size={13} icon={ICONS.CHECK_CIRCLE}/>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );

    return (
        <>
            <div className='ui-filter' ref={ref}>
                <div className="ui-filter__inner" onClick={handleToggle}>
                    <div className="ui-filter__label">
                        {!!label && label}
                        {!label && (
                            <>
                                <UiIcon icon={ICONS.FILTER2} size={16} color={COLORS.BROWN_1}/>
                                <span>Фильтры</span>
                            </>
                        )}
                    </div>
                    {!isGrouped && (
                        <select
                            value={value ? value : ''}
                            onChange={(e) => {
                                const nextValue = e.target.value ? e.target.value : null;
                                handleChange(nextValue);
                            }}
                        >
                            {defaultItem && (
                                <option value={defaultItem.id ?? ''}>{defaultItem.name}</option>
                            )}
                            {items.map(item => (
                                <option key={item.id} value={item.id as string}>{item.name}</option>
                            ))}
                        </select>
                    )}
                </div>
                {store.isOpened && !isGrouped && (
                    <div className="ui-filter__items">
                        {defaultItem && (
                            <div className="ui-filter-item" onClick={() => handleChange(null)}>
                                <div className="ui-filter-item__name">{defaultItem.name}</div>
                                {isDefaultSingleValue(value, defaultItem) && (
                                    <div className="ui-filter-item__icon">
                                        <UiIcon size={13} icon={ICONS.CHECK_CIRCLE}/>
                                    </div>
                                )}
                            </div>
                        )}
                        {items.map(item => (
                            <div
                                key={item.id}
                                className="ui-filter-item"
                                onClick={() => handleChange(item.id)}
                            >
                                <div className="ui-filter-item__name">{item.name}</div>
                                {item.id === value && (
                                    <div className="ui-filter-item__icon">
                                        <UiIcon size={13} icon={ICONS.CHECK_CIRCLE}/>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
                {store.isOpened && isGrouped && !isGroupedMobile && renderGroupedItems()}
            </div>
            {isGroupedMobile && (
                <UiModal
                    isOpened={store.isOpened}
                    onClose={handleClose}
                    title='Фильтры'
                    className='ui-filter-modal'
                >
                    {renderGroupedItems('ui-filter__items--modal')}
                </UiModal>
            )}
        </>
    );
});
